import {
  ChangeDetectionStrategy,
  Component,
  Signal,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from '@auth0/auth0-angular';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MenuItem } from 'primeng/api';
import { Avatar } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { TieredMenuModule } from 'primeng/tieredmenu';
import { ToolbarModule } from 'primeng/toolbar';
import { PermissionService } from '../../core/auth/permission.service';
import { SUPPORTED_LANGUAGES, SupportedLanguage } from '../../core/i18n/translation.config';
import { ThemeService } from '../../core/theme/theme.service';

@Component({
  selector: 'app-header',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ToolbarModule, ButtonModule, TieredMenuModule, Avatar, TranslatePipe],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent {
  private readonly auth = inject(AuthService);
  private readonly translate = inject(TranslateService);
  private readonly theme = inject(ThemeService);
  private readonly permissions = inject(PermissionService);

  /** Tracks the active language so the (non-reactive) menu labels rebuild. */
  private readonly currentLang = signal<string>(
    this.translate.getCurrentLang() || SUPPORTED_LANGUAGES[0],
  );

  /** Display name resolved from the Auth0 user profile. */
  readonly displayName: Signal<string> = computed(() => {
    const user = this.permissions.user();
    return user?.name ?? user?.nickname ?? user?.email ?? 'Account';
  });

  /** Tiered menu models, recomputed whenever language, theme or user change. */
  readonly menuModel: Signal<MenuItem[]> = computed(() => {
    // Re-run when the language changes so labels are re-translated.
    this.currentLang();
    const activeThemeId = this.theme.activeTheme().id;
    const t = (key: string): string => this.translate.instant(key);

    return [
      { label: this.displayName(), icon: 'pi pi-user', disabled: true },
      { separator: true },
      {
        label: t('header.theme.title'),
        icon: 'pi pi-palette',
        items: this.theme.themes.map((theme) => ({
          label: t(theme.labelKey),
          icon: activeThemeId === theme.id ? 'pi pi-check' : 'pi pi-circle',
          command: () => this.theme.setTheme(theme.id),
        })),
      },
      {
        label: t('header.language.title'),
        icon: 'pi pi-globe',
        items: SUPPORTED_LANGUAGES.map((lang) => ({
          label: t(`header.language.${lang}`),
          icon: this.currentLang() === lang ? 'pi pi-check' : 'pi pi-circle',
          command: () => this.switchLanguage(lang),
        })),
      },
      { separator: true },
      {
        label: t('header.logout'),
        icon: 'pi pi-sign-out',
        command: () => this.logout(),
      },
    ];
  });

  constructor() {
    // Update once the new language is actually applied (translations loaded).
    this.translate.onLangChange
      .pipe(takeUntilDestroyed())
      .subscribe((event) => this.currentLang.set(event.lang));
  }

  switchLanguage(lang: SupportedLanguage): void {
    this.translate.use(lang);
  }

  logout(): void {
    this.auth.logout({ logoutParams: { returnTo: window.location.origin } });
  }
}
